import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { I18nContext, type I18nContextValue, type Locale } from "./I18nContext";
import plTranslations from "../locale-files/pl.json";
import enTranslations from "../locale-files/en.json";

const LOCALE_STORAGE_KEY = "app-locale";

const translations: Record<Locale, Record<string, string>> = {
  "pl-PL": plTranslations,
  "en-US": enTranslations,
};

function readStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored === "en-US" || stored === "pl-PL") return stored;
  } catch { /* ignore */ }
  return "pl-PL";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readStoredLocale);

  const setLocale = useCallback((next: Locale) => {
    try { localStorage.setItem(LOCALE_STORAGE_KEY, next); } catch { /* ignore */ }
    setLocaleState(next);
  }, []);

  const isEnglish = locale === "en-US";

  useEffect(() => {
    document.documentElement.lang = isEnglish ? "en" : "pl";
  }, [isEnglish]);

  const value = useMemo<I18nContextValue>(() => ({
    locale,
    isEnglish,
    setLocale,
    t: (text: string) => translations[locale][text] ?? text
  }), [isEnglish, locale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

